/**
 * TicketTermico — Página imprimible optimizada para impresoras térmicas 80mm.
 *
 * URL: /carwash/turnos/:id/ticket-termico
 *
 * Al cargar, hace fetch del turno y auto-dispara window.print() con CSS @page 80mm.
 * Compatible con Epson TM-T20, Xprinter, Star Micronics, etc. vía dialog del navegador.
 * Feature-gated: bloqueada si el tenant no tiene `ticket_termico`.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api, { formatoMXN } from '@/lib/api';
import { useAuth } from '@/contexto/AuthContexto';
import { Lock } from 'lucide-react';

export default function TicketTermico() {
  const { id } = useParams();
  const { featuresPlan } = useAuth();
  const [turno, setTurno] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');

  const tieneFeature = featuresPlan === null || featuresPlan.includes('ticket_termico');

  useEffect(() => {
    if (!tieneFeature) return;
    (async () => {
      try {
        const [tRes, cRes] = await Promise.all([
          api.get(`/carwash/turnos/${id}`),
          api.get('/configuracion').catch(() => ({ data: {} })),
        ]);
        setTurno(tRes.data);
        setConfig(cRes.data || {});
      } catch (e) {
        setError(e.response?.data?.detail || 'No se pudo cargar el turno');
      }
    })();
  }, [id, tieneFeature]);

  useEffect(() => {
    if (turno) {
      // Delay para asegurar render antes de imprimir
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [turno]);

  if (!tieneFeature) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="max-w-sm bg-slate-900 border border-slate-800 rounded-lg p-6 text-center" data-testid="ticket-termico-locked">
          <Lock className="w-8 h-8 text-amber-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-100 mb-2">Ticket térmico no disponible</h2>
          <p className="text-sm text-slate-400 mb-4">Esta función no está incluida en tu plan actual.</p>
          <a href="/suscripcion" className="inline-block bg-amber-500 hover:bg-amber-600 text-black font-semibold px-4 py-2 rounded">
            Ver planes
          </a>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="min-h-screen bg-white flex items-center justify-center text-red-600 text-sm">{error}</div>;
  }
  if (!turno) {
    return <div className="min-h-screen bg-white flex items-center justify-center text-slate-500 text-sm">Cargando...</div>;
  }

  const productos = turno.productos_extra || [];
  const totalProductos = productos.reduce((s, p) => s + (p.subtotal || 0), 0);
  const cargoExtra = turno.cargo_extra_monto || 0;
  // precio_final es la fuente de verdad (ya incluye productos, descuento y cargo extra).
  const total = turno.precio_final != null
    ? turno.precio_final
    : (turno.precio || 0) + totalProductos + cargoExtra;

  // Valores por defecto: mostrar todo si el dueño no ha configurado
  const t = {
    mostrar_logo: config?.ticket_mostrar_logo !== false,
    mostrar_direccion: config?.ticket_mostrar_direccion !== false,
    mostrar_telefono: config?.ticket_mostrar_telefono !== false,
    mostrar_rfc: config?.ticket_mostrar_rfc !== false,
    mostrar_cliente: config?.ticket_mostrar_cliente !== false,
    mostrar_operario: config?.ticket_mostrar_operario !== false,
    mostrar_placa: config?.ticket_mostrar_placa !== false,
    mostrar_metodo_pago: config?.ticket_mostrar_metodo_pago !== false,
    encabezado: config?.ticket_mensaje_encabezado || '',
    pie: config?.ticket_mensaje_pie || 'Gracias por su preferencia',
    terminos: config?.ticket_terminos_condiciones || '',
    ancho: config?.ticket_ancho_mm || 80,
  };

  return (
    <div className="ticket-container" data-testid="ticket-termico-body">
      <style>{`
        @page { size: ${t.ancho}mm auto; margin: 0; }
        @media print {
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .ticket-container {
            width: ${t.ancho}mm; padding: 2mm 2mm; margin: 0;
            -webkit-print-color-adjust: exact; print-color-adjust: exact;
          }
        }
        body { background: #f1f5f9; }
        .ticket-container {
          font-family: 'Courier New', 'Consolas', monospace;
          width: ${t.ancho}mm;
          max-width: 100%;
          padding: 2mm 2mm;
          margin: 0 auto;
          background: white;
          color: #000;
          font-size: 11pt;
          line-height: 1.25;
          word-break: break-word;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .row { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; }
        /* Etiqueta de info nunca se parte; el valor se alinea a la derecha y puede envolver */
        .row > span:first-child { white-space: nowrap; flex: 0 0 auto; }
        .row > span:last-child { text-align: right; word-break: break-word; }
        /* Artículo (servicio/producto/cargo): nombre truncado a la izquierda, precio firme a la derecha, todo en un renglón */
        .item { display: flex; justify-content: space-between; align-items: baseline; gap: 4px; }
        .item-name { flex: 1 1 auto; min-width: 0; max-width: 72%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .item-price { flex: 0 0 auto; text-align: right; white-space: nowrap; }
        .sep { border-top: 1px dashed #000; margin: 4px 0; }
        .lg { font-size: 14pt; }
        .sm { font-size: 10pt; }
        .terms { font-size: 9pt; white-space: pre-wrap; margin-top: 4px; }
        .actions { position: fixed; bottom: 8px; left: 8px; right: 8px; text-align: center; }
        .actions button { background: #0891b2; color: white; padding: 8px 16px; border: 0; border-radius: 6px; cursor: pointer; margin: 0 4px; font-family: system-ui; font-size: 14px; }
        .logo { max-width: 60%; max-height: 40px; margin: 0 auto 4px; display: block; }
      `}</style>

      {t.mostrar_logo && config?.logo_url && (
        <img src={`${config.logo_url.startsWith('http') ? config.logo_url : `${process.env.REACT_APP_BACKEND_URL}${config.logo_url}`}?v=${encodeURIComponent(config.logo_actualizado_en || '')}`}
          alt="" className="logo" />
      )}
      <div className="center bold lg">{config?.nombre_taller || 'Car Wash'}</div>
      {t.mostrar_direccion && config?.direccion && <div className="center sm">{config.direccion}</div>}
      {t.mostrar_telefono && config?.telefono && <div className="center sm">Tel: {config.telefono}</div>}
      {t.mostrar_rfc && config?.rfc && <div className="center sm">RFC: {config.rfc}</div>}
      {t.encabezado && <div className="center sm" style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{t.encabezado}</div>}
      <div className="sep" />

      <div className="row"><span>Folio:</span><span className="bold">{turno.numero_turno}</span></div>
      <div className="row"><span>Fecha:</span><span>{new Date(turno.fecha_ingreso || turno.created_at).toLocaleString('es-MX')}</span></div>
      {t.mostrar_cliente && turno.cliente_nombre && <div className="row"><span>Cliente:</span><span>{turno.cliente_nombre}</span></div>}
      {t.mostrar_placa && <div className="row"><span>Placa:</span><span className="bold">{turno.placa}</span></div>}
      {turno.color && <div className="row"><span>Color:</span><span>{turno.color}</span></div>}
      <div className="row"><span>Tipo:</span><span>{turno.tipo_vehiculo}</span></div>
      {t.mostrar_operario && turno.operario_nombre && <div className="row"><span>Operario:</span><span>{turno.operario_nombre}</span></div>}

      <div className="sep" />
      <div className="bold">SERVICIOS</div>
      <div className="item"><span className="item-name">- {turno.servicio_nombre || 'Servicio'}</span><span className="item-price">{formatoMXN(turno.precio || 0)}</span></div>

      {productos.length > 0 && (
        <>
          <div className="sep" />
          <div className="bold">PRODUCTOS</div>
          {productos.map((p, i) => (
            <div className="item" key={i}>
              <span className="item-name">- {p.cantidad}x {p.nombre}</span>
              <span className="item-price">{formatoMXN(p.subtotal || 0)}</span>
            </div>
          ))}
        </>
      )}

      <div className="sep" />
      {turno.descuento_monto > 0 && (
        <div className="item"><span className="item-name">- Descuento</span><span className="item-price">-{formatoMXN(turno.descuento_monto)}</span></div>
      )}
      {turno.cargo_extra_monto > 0 && (
        <div className="item"><span className="item-name">- {turno.cargo_extra_descripcion || 'Cargo extra'}</span><span className="item-price">{formatoMXN(turno.cargo_extra_monto)}</span></div>
      )}
      <div className="row bold lg"><span>TOTAL</span><span>{formatoMXN(total)}</span></div>
      {t.mostrar_metodo_pago && turno.metodo_pago && <div className="row"><span>Pago:</span><span className="bold">{turno.metodo_pago.toUpperCase()}</span></div>}

      <div className="sep" />
      <div className="center sm">{t.pie}</div>
      {t.terminos && <div className="terms">{t.terminos}</div>}
      <div className="center sm">{new Date().toLocaleString('es-MX')}</div>
      <div style={{ height: '10mm' }} />

      <div className="actions no-print">
        <button onClick={() => window.print()} data-testid="btn-reimprimir">Imprimir</button>
        <button onClick={() => window.close()} data-testid="btn-cerrar">Cerrar</button>
      </div>
    </div>
  );
}
